import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User, Phone, MapPin, School, BookOpen, FileText, Loader2, ArrowLeft } from 'lucide-react';

const GOVERNORATES = ["الدقهلية", "القاهرة", "الجيزة", "الإسكندرية", "القليوبية", "الشرقية", "المنوفية", "الغربية", "دمياط", "كفر الشيخ", "البحيرة", "الإسماعيلية", "بورسعيد", "السويس", "مطروح", "شمال سيناء", "جنوب سيناء", "بني سويف", "الفيوم", "المنيا", "أسيوط", "سوهاج", "قنا", "الأقصر", "أسوان", "الوادي الجديد", "البحر الأحمر"];

export const CompleteProfile = () => {
  const { user, profile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    firstName: profile?.firstName || '',
    middleName: profile?.middleName || '',
    lastName: profile?.lastName || '',
    phone: profile?.phone || '',
    father_phone: profile?.father_phone || '',
    mother_phone: profile?.mother_phone || '',
    school: profile?.school || '',
    governorate: profile?.governorate || '',
    grade: profile?.grade || '2sec',
    bio: profile?.bio || ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Arabic-only for name fields
    if (['firstName', 'middleName', 'lastName'].includes(name)) {
      if (/^[\u0600-\u06FF\s]*$/.test(value) || value === '') {
        setFormData({ ...formData, [name]: value });
      }
    }
    // Numbers-only for phone fields
    else if (['phone', 'father_phone', 'mother_phone'].includes(name)) {
      if (/^\d*$/.test(value) && value.length <= 11) {
        setFormData({ ...formData, [name]: value });
      }
    }
    else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.middleName || !formData.lastName) {
      setError('⚠️ يجب كتابة الاسم الثلاثي بالكامل');
      return false;
    }
    if (formData.phone.length !== 11) {
      setError('⚠️ رقم الهاتف يجب أن يكون 11 رقم');
      return false;
    }
    if (!formData.governorate) {
      setError('⚠️ من فضلك اختر المحافظة');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const result = await updateProfile({
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        phone: formData.phone,
        father_phone: formData.father_phone || '',
        mother_phone: formData.mother_phone || '',
        school: formData.school || '',       // اختياري
        bio: formData.bio || '',             // اختياري
        governorate: formData.governorate,
        grade: formData.grade
      });

      if (result.success) {
        // Redirect based on role
        if (profile?.role === 'admin' || profile?.role === 'boss') {
          navigate('/admin');
        } else {
          navigate('/student');
        }
      } else {
        setError(result.error || 'حدث خطأ في حفظ البيانات');
      }
    } catch (err) {
      setError(err.message || 'حدث خطأ غير متوقع');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 py-8" dir="rtl">
      {/* Background glow */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-white/[0.03] blur-[120px] rounded-full"></div>
      </div>

      <div className="w-full max-w-2xl relative z-10">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full mx-auto mb-4 border-2 border-white/10 bg-white/5 flex items-center justify-center">
            <User size={36} className="text-white/40" />
          </div>
          <h2 className="text-3xl font-black text-white mb-2">أهلاً بك يا بطل! 👋</h2>
          <p className="text-zinc-500 text-sm">استكمل بياناتك بدقة عشان حسابك يشتغل</p>
        </div>

        <div className="bg-zinc-900/40 backdrop-blur-3xl border border-white/10 p-6 md:p-10 rounded-[32px] shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* الاسم الثلاثي */}
            <div>
              <h4 className="text-xs font-bold text-zinc-400 mb-3 flex items-center gap-2">
                <User size={14} /> الاسم الثلاثي (باللغة العربية)
              </h4>
              <div className="grid md:grid-cols-3 gap-3">
                <input name="firstName" value={formData.firstName} onChange={handleInputChange}
                  placeholder="الاسم الأول" required
                  className="w-full p-4 bg-white/[0.03] border border-white/10 rounded-2xl text-white text-center text-sm focus:border-white/30 outline-none" />
                <input name="middleName" value={formData.middleName} onChange={handleInputChange}
                  placeholder="اسم الأب" required
                  className="w-full p-4 bg-white/[0.03] border border-white/10 rounded-2xl text-white text-center text-sm focus:border-white/30 outline-none" />
                <input name="lastName" value={formData.lastName} onChange={handleInputChange}
                  placeholder="اسم الجد" required
                  className="w-full p-4 bg-white/[0.03] border border-white/10 rounded-2xl text-white text-center text-sm focus:border-white/30 outline-none" />
              </div>
            </div>

            {/* أرقام الهواتف */}
            <div className="grid md:grid-cols-3 gap-3">
              <div className="relative">
                <Phone className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <input name="phone" value={formData.phone} onChange={handleInputChange}
                  placeholder="رقمك (11 رقم)" maxLength="11" required
                  className="w-full p-4 pr-11 bg-white/[0.03] border border-white/10 rounded-2xl text-white text-sm focus:border-white/30 outline-none" />
              </div>
              <div className="relative">
                <Phone className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <input name="father_phone" value={formData.father_phone} onChange={handleInputChange}
                  placeholder="موبايل الأب" maxLength="11"
                  className="w-full p-4 pr-11 bg-white/[0.03] border border-white/10 rounded-2xl text-white text-sm focus:border-white/30 outline-none" />
              </div>
              <div className="relative">
                <Phone className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <input name="mother_phone" value={formData.mother_phone} onChange={handleInputChange}
                  placeholder="موبايل الأم" maxLength="11"
                  className="w-full p-4 pr-11 bg-white/[0.03] border border-white/10 rounded-2xl text-white text-sm focus:border-white/30 outline-none" />
              </div>
            </div>

            {/* المحافظة والمدرسة */}
            <div className="grid md:grid-cols-2 gap-3">
              <div className="relative">
                <MapPin className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <select name="governorate" value={formData.governorate} onChange={handleInputChange} required
                  className="w-full p-4 pr-11 bg-white/[0.03] border border-white/10 rounded-2xl text-white text-sm focus:border-white/30 outline-none appearance-none">
                  <option value="" className="bg-black">المحافظة</option>
                  {GOVERNORATES.map(g => <option key={g} value={g} className="bg-black">{g}</option>)}
                </select>
              </div>
              <div className="relative">
                <School className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <input name="school" value={formData.school} onChange={handleInputChange}
                  placeholder="اسم المدرسة (اختياري)"
                  className="w-full p-4 pr-11 bg-white/[0.03] border border-white/10 rounded-2xl text-white text-sm focus:border-white/30 outline-none" />
              </div>
            </div>

            {/* الصف الدراسي */}
            <div className="relative">
              <BookOpen className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
              <select name="grade" value={formData.grade} onChange={handleInputChange} required
                className="w-full p-4 pr-11 bg-white/[0.03] border border-white/10 rounded-2xl text-white text-sm focus:border-white/30 outline-none appearance-none">
                <option value="1sec" className="bg-black">الصف الأول الثانوي</option>
                <option value="2sec" className="bg-black">الصف الثاني الثانوي</option>
                <option value="3sec" className="bg-black">الصف الثالث الثانوي</option>
              </select>
            </div>

            {/* نبذة (اختياري) */}
            <div>
              <h4 className="text-xs font-bold text-zinc-500 mb-2 flex items-center gap-2">
                <FileText size={14} /> نبذة عنك (اختياري)
              </h4>
              <textarea name="bio" value={formData.bio} onChange={handleInputChange}
                placeholder="اكتب وصف لنفسك..."
                className="w-full p-4 bg-white/[0.03] border border-white/10 rounded-2xl text-white text-sm h-24 resize-none focus:border-white/30 outline-none" />
            </div>

            {error && <p className="text-red-400 text-sm text-center bg-red-500/10 border border-red-500/20 p-3 rounded-2xl">{error}</p>}

            <button type="submit" disabled={loading}
              className="w-full bg-white text-black font-black py-5 rounded-2xl hover:bg-zinc-200 transition-all flex items-center justify-center gap-3 disabled:opacity-50 active:scale-95 shadow-[0_0_20px_rgba(255,255,255,0.1)]">
              {loading ? <Loader2 className="animate-spin" size={20} /> : (
                <>
                  <span>حفظ البيانات والمتابعة</span>
                  <ArrowLeft size={20} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};